// Search-state for the resale-items index route. The route in AppRouter.tsx owns
// the validateSearch schema and stripSearchParams middleware; this hook is a
// thin, typed surface over TanStack's useSearch / useNavigate so consumers don't
// touch the router primitives directly. Switching status resets the page so a
// previous filter's pagination never bleeds into the new tab.

// External
import { useCallback, useMemo } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

// Internal
import { DEFAULT_PAGE_SIZE } from '@components/DataTable/constants';
import {
  RESALE_ITEM_STATUS_VALUES,
  type ResaleItemStatus,
} from '../components/ResaleItem/types';

export interface ResaleItemsSearchParams {
  status: ResaleItemStatus;
  q: string;
  page: number;
  perPage: number;
}

export const RESALE_ITEMS_SEARCH_DEFAULTS: ResaleItemsSearchParams = {
  status: 'active',
  q: '',
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
};

const RESALE_ITEMS_ROUTE = '/resale_items' as const;

// validateSearch handler — coerces unknown URL input into the typed shape.
export function parseResaleItemsSearch(
  raw: Record<string, unknown>,
): ResaleItemsSearchParams {
  const status: ResaleItemStatus = (
    RESALE_ITEM_STATUS_VALUES as readonly string[]
  ).includes(String(raw.status))
    ? (raw.status as ResaleItemStatus)
    : RESALE_ITEMS_SEARCH_DEFAULTS.status;
  const page = Number(raw.page);
  const perPage = Number(raw.perPage);
  return {
    status,
    q: typeof raw.q === 'string' ? raw.q : RESALE_ITEMS_SEARCH_DEFAULTS.q,
    page: Number.isFinite(page) && page > 0 ? page : RESALE_ITEMS_SEARCH_DEFAULTS.page,
    perPage:
      Number.isFinite(perPage) && perPage > 0
        ? perPage
        : RESALE_ITEMS_SEARCH_DEFAULTS.perPage,
  };
}

export function useResaleItemsRouterSearch() {
  const search = useSearch({ from: RESALE_ITEMS_ROUTE });
  const tanNavigate = useNavigate({ from: RESALE_ITEMS_ROUTE });

  const navigate = useCallback(
    (updates: Partial<ResaleItemsSearchParams>) => {
      tanNavigate({
        search: (prev) => ({ ...prev, ...updates }),
        replace: true,
      });
    },
    [tanNavigate],
  );

  return useMemo(
    () => ({
      search,
      navigate,
      setStatus: (status: ResaleItemStatus) => navigate({ status, page: 1 }),
      setQuery: (q: string) => navigate({ q, page: 1 }),
      setPage: (page: number) => navigate({ page }),
      setPerPage: (perPage: number) => navigate({ perPage, page: 1 }),
    }),
    [search, navigate],
  );
}
